import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { getProvidersLabels, providerFields } from '../../constants';
import type { ProviderFormState } from '../../types';
import { filterSelectedExportTypes } from '../utils';

type ConfirmationDialogContentProps = {
  formValues: ProviderFormState;
  providerName: string;
  touchedFields: string[];
};

export const ConfirmationDialogContent = ({
  formValues,
  providerName,
  touchedFields,
}: ConfirmationDialogContentProps) => {
  const { t } = useTranslation();
  const providerFieldsLabels = getProvidersLabels(t);

  const getAllowedExportTypesValues = () =>
    filterSelectedExportTypes(formValues[providerFields.ALLOWED_EXPORT_TYPES]).join(', ') || t('None');

  const getAllowedAccountsValues = () =>
    formValues[providerFields.ALLOWED_ACCOUNTS]?.map(({ name }) => name).join(', ') || t('None');

  return (
    <DialogContent dividers>
      <Typography>
        {t('You intend to save changes under the following provider:')}{' '}
        <Typography variant="body1Bold" component="span">
          {providerName}
        </Typography>
      </Typography>

      {!!touchedFields.length && (
        <Box
          sx={{
            mt: 2,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="textSecondary" data-test="edit-provider-message-details">
            {t('Updated field{suffix}', { suffix: touchedFields.length > 1 ? 's' : '' })}:
          </Typography>

          <Box
            data-test="edit-provider-changes-details"
            sx={{
              mt: 1,
              mb: 1,
            }}
          >
            {touchedFields.map((formField, key) => (
              <Box
                key={`${formField}-${key}`}
                sx={{
                  mb: 1,
                }}
              >
                <Typography variant="body1Bold" component="span">
                  {providerFieldsLabels[formField]}:{' '}
                </Typography>

                <Typography component="span">
                  {formField === providerFields.NAME && formValues[providerFields.NAME]}
                  {formField === providerFields.ALLOWED_EXPORT_TYPES && getAllowedExportTypesValues()}
                  {formField === providerFields.ALLOWED_ACCOUNTS && getAllowedAccountsValues()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {!touchedFields.length && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            mb: 2,
            mt: 2,
          }}
        >
          {t('No changes to apply')}
        </Typography>
      )}

      <Typography>{t('Do you want to proceed?')}</Typography>
    </DialogContent>
  );
};
